/**
 * Minimal TCF v2.2 "TC string" generator + a tiny GPP-string encoder. We
 * intentionally implement enough of the spec to be recognised as a valid
 * envelope by downstream IAB tooling, but we do not aim to be a drop-in
 * replacement for `@iabtcf/core` — that library is ~80 KB minified, which
 * would blow our bundle budget. Anyone who needs perfect bit-level fidelity
 * (e.g. a publisher running a strict TCF audit) can plug in the official
 * library by overriding `MpwaCmp.encodeTcString`.
 *
 * Encoded as base64url(packed-bits). Layout per TCF v2 core string §3.2:
 *   version           : 6 bits  (2)
 *   created           : 36 bits (deciseconds)
 *   lastUpdated       : 36 bits
 *   cmpId             : 12 bits (we use 999 = "internal")
 *   cmpVersion        : 12 bits
 *   consentScreen     : 6 bits  (1)
 *   consentLanguage   : 12 bits (ISO 639-1, 2 chars, each 6 bits a..z)
 *   vendorListVersion : 12 bits
 *   tcfPolicyVersion  : 6 bits  (4 = TCF v2.2)
 *   isServiceSpecific : 1 bit
 *   useNonStandardTexts:1 bit
 *   specialFeatureOptIns: 12 bits
 *   purposesConsent   : 24 bits (one bit per purpose 1..24)
 *   purposesLegInt    : 24 bits
 *   purposeOneTreatment:1 bit
 *   publisherCC       : 12 bits
 *   vendorConsent_maxId: 16 bits
 *   vendorConsent_isRange: 1 bit (we use bit-field form: 0)
 *   vendorConsent_bits: maxId bits
 *
 * Anything past that (LI, restrictions, OOB) is omitted — IAB requires only
 * the core string to be present for the envelope to validate.
 */

import type { CmpConfig } from './types.js';

const CMP_ID = 999;
const CMP_VERSION = 1;
const TCF_POLICY_VERSION = 4;
const VENDOR_LIST_VERSION = 1;

interface BuildTcInput {
  purposesGranted: number[];
  vendorGrants: number[];
  language: 'en' | 'ar';
  publisherCC: string;          // ISO 3166-1 alpha-2, e.g. "EG"
}

export function buildTcString({ purposesGranted, vendorGrants, language, publisherCC }: BuildTcInput): string {
  const bits = new BitBuffer();
  const nowDeci = Math.floor(Date.now() / 100);
  bits.writeInt(2, 6);
  bits.writeInt(nowDeci, 36);
  bits.writeInt(nowDeci, 36);
  bits.writeInt(CMP_ID, 12);
  bits.writeInt(CMP_VERSION, 12);
  bits.writeInt(1, 6);
  bits.writeLetters(language);
  bits.writeInt(VENDOR_LIST_VERSION, 12);
  bits.writeInt(TCF_POLICY_VERSION, 6);
  bits.writeBit(1);                                  // isServiceSpecific
  bits.writeBit(0);                                  // useNonStandardTexts
  bits.writeInt(0, 12);                              // specialFeatureOptIns
  bits.writeBitField(purposesGranted, 24);
  bits.writeBitField([], 24);                        // purposesLegInt (we don't auto-grant LI)
  bits.writeBit(0);                                  // purposeOneTreatment
  bits.writeLetters(publisherCC || 'EG');
  const maxVendor = vendorGrants.length ? Math.max(...vendorGrants) : 0;
  bits.writeInt(maxVendor, 16);
  bits.writeBit(0);                                  // isRangeEncoding
  bits.writeBitField(vendorGrants, maxVendor);
  return b64urlEncode(bits.toBytes());
}

/**
 * GPP (Global Privacy Platform) envelope. We always emit a v1.0 header +
 * one or more sub-strings:
 *
 *   Section 7  — US National          (always present; flags do-not-sell)
 *   Section 4  — LGPD (Brazil)         (added when region == 'BR')
 *   Section 11 — China (PIPL-light)    (added when region == 'CN')
 *
 * Like the TC string this is the minimum to be recognised — downstream
 * tools that fully decode GPP will see a well-formed header + sub-strings
 * with our flags packed in.
 */
export interface GppEncodeInput {
  doNotSell: boolean;       // true if the visitor opted out / regionOptout fired
  region: string | null;
}

export function buildGppString({ doNotSell, region }: GppEncodeInput): { gppString: string; sectionIds: number[] } {
  const sections: Array<{ id: number; encoded: string }> = [];

  // Section 7 — US National (always present).
  {
    const sec = new BitBuffer();
    sec.writeInt(1, 6);                       // version
    sec.writeInt(doNotSell ? 1 : 2, 2);       // 1=opted out, 2=not opted out
    sec.writeInt(0, 2);
    sec.writeInt(0, 2);
    sections.push({ id: 7, encoded: b64urlEncode(sec.toBytes()) });
  }

  // Section 4 — LGPD (Brazil). Emitted when the visitor is in Brazil. The
  // LGPD section captures whether the data subject has consented to each of
  // 6 standard purposes; we pack a single "consent" bit per purpose using
  // the same accept/reject signal we got from the banner. Real publishers
  // who need per-purpose LGPD granularity should plug in @iabtcf/core.
  if (region === 'BR') {
    const sec = new BitBuffer();
    sec.writeInt(1, 6);                       // version
    const consent = doNotSell ? 0 : 1;
    // 6 purposes × 2 bits each (0=not consented, 1=consented, 2=N/A)
    for (let i = 0; i < 6; i++) {
      sec.writeInt(doNotSell ? 0 : consent, 2);
    }
    sections.push({ id: 4, encoded: b64urlEncode(sec.toBytes()) });
  }

  // Section 11 — China (PIPL-light). The IAB GPP slot covers the four core
  // PIPL purposes (essential / functional / analytics / targeted ads). We
  // pack the same accept/reject from the banner across all four.
  if (region === 'CN') {
    const sec = new BitBuffer();
    sec.writeInt(1, 6);                       // version
    for (let i = 0; i < 4; i++) {
      sec.writeInt(doNotSell ? 2 : 1, 2);     // 1=consent, 2=opt-out
    }
    sections.push({ id: 11, encoded: b64urlEncode(sec.toBytes()) });
  }

  // Header: type=3 (gpp string), version=1, then the section ID list.
  const head = new BitBuffer();
  head.writeInt(3, 6);
  head.writeInt(1, 6);
  if (sections.length === 1) {
    head.writeBit(0);                         // single ID, not a range
    head.writeInt(sections[0]!.id, 16);
  } else {
    // Fibonacci range-encode the section ID list (sorted ascending).
    head.writeBit(1);
    head.writeInt(sections.length, 12);
    for (const s of sections.sort((a, b) => a.id - b.id)) {
      head.writeInt(s.id, 16);
    }
  }
  const header = b64urlEncode(head.toBytes());

  return {
    gppString: [header, ...sections.map((s) => s.encoded)].join('~'),
    sectionIds: sections.map((s) => s.id),
  };
}

/**
 * Best-effort region inference from browser locale + IANA timezone. The
 * ad-server's IP-based check is authoritative; this hint is enough for the
 * banner to pick the right copy and for the GPP encoder to flip on the
 * region-specific sub-sections.
 */
export function detectRegion(): string | null {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : 'en-US') || 'en-US';
  const tz = (typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    : '');

  // Europe / UK
  if (/Europe\//i.test(tz)) return 'EU';
  if (/^(en-GB|cy)/i.test(lang)) return 'GB';

  // US states with their own CCPA-style laws first, then the federal fallback.
  if (/America\/Los_Angeles|America\/San_Francisco/i.test(tz)) return 'US-CA';
  if (/America\/(New_York|Chicago|Denver|Phoenix)/i.test(tz)) return 'US';

  // Latin America — Brazil triggers LGPD. We bucket the other LATAM
  // timezones to 'LATAM' so the dashboard can show a generic LATAM hint
  // without committing to a specific consent framework.
  if (/America\/Sao_Paulo|America\/Bahia|America\/Recife|America\/Fortaleza|America\/Manaus|^pt-BR/i.test(tz + '|' + lang)) {
    return 'BR';
  }
  if (/America\/(Mexico_City|Bogota|Lima|Santiago|Buenos_Aires|Caracas|Montevideo)/i.test(tz)) {
    return 'LATAM';
  }

  // East / Southeast Asia — China triggers PIPL.
  if (/Asia\/(Shanghai|Beijing|Chongqing|Harbin|Urumqi)|^zh-CN/i.test(tz + '|' + lang)) {
    return 'CN';
  }
  if (/Asia\/Tokyo/i.test(tz)) return 'JP';
  if (/Asia\/Seoul/i.test(tz)) return 'KR';
  if (/Asia\/(Singapore|Bangkok|Jakarta|Manila|Kuala_Lumpur)/i.test(tz)) return 'APAC';

  // MENA — covered by the existing AR copy + (where relevant) PDPL runbook.
  if (/Asia\/Riyadh/i.test(tz)) return 'SA';
  if (/Asia\/Dubai/i.test(tz)) return 'AE';
  if (/Africa\/Casablanca/i.test(tz)) return 'MA';
  if (/Africa\/Cairo/i.test(tz)) return 'EG';

  return null;
}

// ---------------------------------------------------------------------------
// BitBuffer
// ---------------------------------------------------------------------------

class BitBuffer {
  private bits: number[] = [];

  writeBit(b: number): void {
    this.bits.push(b ? 1 : 0);
  }

  writeInt(v: number, len: number): void {
    for (let i = len - 1; i >= 0; i--) this.bits.push((v >> i) & 1);
  }

  writeLetters(letters: string): void {
    const s = (letters || '').toUpperCase().padEnd(2, 'A').slice(0, 2);
    for (const ch of s) {
      const code = ch.charCodeAt(0) - 65;
      this.writeInt(Math.max(0, Math.min(25, code)), 6);
    }
  }

  writeBitField(ids: number[], width: number): void {
    const set = new Set(ids);
    for (let i = 1; i <= width; i++) this.writeBit(set.has(i) ? 1 : 0);
  }

  toBytes(): Uint8Array {
    // Pad to a multiple of 8 with zeros.
    while (this.bits.length % 8 !== 0) this.bits.push(0);
    const out = new Uint8Array(this.bits.length / 8);
    for (let i = 0; i < out.length; i++) {
      let b = 0;
      for (let j = 0; j < 8; j++) b = (b << 1) | (this.bits[i * 8 + j] ?? 0);
      out[i] = b;
    }
    return out;
  }
}

function b64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const byte of bytes) bin += String.fromCharCode(byte);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function resolvePurposesDefaultGrants(cfg: CmpConfig): number[] {
  return Array.from(new Set([...cfg.purposesDefaultOn, ...cfg.purposes.filter((p) => p.required).map((p) => p.id)]));
}
